import {
	type SessionFlowSlot,
	type SkillCreateFlowSessionState,
	type SkillCreateFlowStage,
	type SkillFlowSessionHandle,
	type SkillFlowTouchPoint,
	TOUCH_POINT_SESSION_SLOT,
} from './types';

const state = new Map<SessionFlowSlot, SkillCreateFlowSessionState>();

const createHandle = (slot: SessionFlowSlot): SkillFlowSessionHandle => ({
	get slot() {
		return slot;
	},
	get state() {
		return state.get(slot);
	},
	// Updates the stored session in place, so every handle to the slot sees the new stage.
	advanceTo(stage: SkillCreateFlowStage) {
		const session = this.state;
		if (session) {
			session.stage = stage;
		}
		return this;
	},
	clear() {
		state.delete(slot);
	},
});

/**
 * The flow spans component trees: the touch point is known where it is viewed or started, but has
 * to reach events fired later by unrelated components, such as the skill draft card. Module scope
 * keeps it available to every `useCustomSkillsAnalytics` consumer without a provider.
 */
export const skillCreateFlowSession = {
	/**
	 * Replaces any session in the touch point's slot, so every call begins a new pass with a new
	 * `traceId`. Sessions in other slots are left alone.
	 */
	start: (touchPoint: SkillFlowTouchPoint, stage: SkillCreateFlowStage): SkillFlowSessionHandle => {
		const sessionSlot = TOUCH_POINT_SESSION_SLOT[touchPoint];
		const sessionState = { traceId: crypto.randomUUID(), touchPoint, stage };

		state.set(sessionSlot, sessionState);

		return createHandle(sessionSlot);
	},
	getFromSessionSlot: (sessionSlot: SessionFlowSlot): SkillFlowSessionHandle | undefined =>
		state.has(sessionSlot) ? createHandle(sessionSlot) : undefined,
	/** Ends every session. Tests need it, as the sessions outlive each rendered hook. */
	clearAll: (): void => state.clear(),
};
